import {
  Box,
  Card,
  Flex,
  Skeleton,
  Tag,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  LimitedDataErrorMessage,
  MissingListPriceErrorMessage,
} from '@common/utils/exceptions';
import { FC, useState } from 'react';
import { ListingDisclaimer } from '~/apps/consumer/pages/Storefront/ListingDisclaimer';
import { useIsDataCoachEnabled } from '~/common/copilot/hooks/isChatEnabled';
import { useIsMobile } from '~/common/hooks/useIsMobile';
import { PreparedBy } from '../../../BuyerOfferAnalysis/PreparedBy';
import { CopilotKit } from '../../../CopilotChat';
import { ListingAgentInformation } from '../../../Storefront/ListingAgent/ListingAgentCard';
import { GenericListingErrorState } from '../Errors/GenericListingErrorState';
import { LimitedDataErrorState } from '../Errors/LimitedDataErrorState';
import { MissingListPriceErrorState } from '../Errors/MissingListPriceErrorState';
import { useListingPageContext } from '../ListingPageContext';
import { ListingPageKeyTermsTable } from '../ListingPageKeyTerms/ListingPageKeyTermsTable';
import { METRIC_CARD_MIN_HEIGHT } from '../ListingPageMetrics/constants';
import { ListingPageMetricsGrid } from '../ListingPageMetrics/ListingPageMetricsGrid';
import { ListingChatModalWrapper } from '../ReportChat/ListingChatModalWrapper';
import { ListingCopilotSuggestions } from '../ReportChat/ListingCopilotSuggestions';
import { Comparables } from './Comparables';
import {
  COMPARABLES_SECTION_ID,
  COMPARABLES_SECTION_TITLE,
  MARKET_TRENDS_SECTION_ID,
  MARKET_TRENDS_SECTION_TITLE,
  MLS_INFO_SECTION_ID,
  MLS_INFO_SECTION_TITLE,
  OFFER_TYPES_SECTION_ID,
  OFFER_TYPES_SECTION_TITLE,
} from './constants';
import { DataSourceTooltip } from './DataSource/DataSourceTooltip';
import { ListingInfo } from './ListingInfo';
import { ListingPageHeader } from './ListingPageHeader';
import { ListingPageSection } from './ListingPageSection';
import { MlsInfo } from './MLSInfo/MlsInfo';
import { ListingInfoMobile } from './Mobile/ListingInfoMobile';
import { ListingPageMobileActionBar } from './Mobile/ListingPageMobileActionBar';
import { ListingPageMobileHeader } from './Mobile/ListingPageMobileHeader';

// Main Component
export const ListingPageContent: FC = () => {
  const isMobile = useIsMobile();
  const { isViewOnly, reportError, listing, isPrint, sharedBy } =
    useListingPageContext();
  const isDataCoachEnabled = useIsDataCoachEnabled();

  // Chat state
  const {
    isOpen: isChatOpen,
    onOpen: onChatOpen,
    onClose: onChatClose,
  } = useDisclosure();
  const [chatInitialPrompt, setChatInitialPrompt] = useState<string>();

  const handleSuggestionClick = (prompt?: string) => {
    setChatInitialPrompt(prompt);
    onChatOpen();
  };

  const mainContent = (
    <>
      {!isViewOnly && isDataCoachEnabled && (
        <Box pt={1} px={{ base: 4, md: 10 }} width="100%">
          <ListingCopilotSuggestions
            onSuggestionClick={handleSuggestionClick}
          />
        </Box>
      )}

      <Flex
        alignItems="flex-start"
        direction="column"
        gap={6}
        position="relative"
        px={isPrint ? 10 : { base: 4, md: 10 }}
        py={2}
        width="100%"
      >
        <ReportContent />
        {listing && (
          <>
            {isMobile && !isPrint && (
              <Card px={4} py={3} width="100%">
                <ListingAgentInformation listing={listing} />
              </Card>
            )}
            <ListingDisclaimer mls={listing?.mlsListing?.mls} />
          </>
        )}
      </Flex>

      {isMobile && !isPrint && <ListingPageMobileActionBar />}

      <ListingChatModalWrapper
        initialPrompt={chatInitialPrompt}
        isOpen={isChatOpen}
        onClose={onChatClose}
      />
    </>
  );

  const renderContent = () => {
    if (reportError) {
      return reportError.message === MissingListPriceErrorMessage ? (
        <MissingListPriceErrorState />
      ) : reportError.message === LimitedDataErrorMessage ? (
        <LimitedDataErrorState />
      ) : (
        <GenericListingErrorState />
      );
    }

    return mainContent;
  };

  const header = () => {
    if (isPrint) {
      return (
        <Flex justifyContent="flex-end" my={6} width="90%">
          <PreparedBy agent={sharedBy} />
        </Flex>
      );
    }

    if (isMobile && isViewOnly) {
      return <ListingPageMobileHeader />;
    }

    if (!isMobile) {
      return <ListingPageHeader />;
    }

    return null;
  };

  return (
    <CopilotKit threadId={listing?.id}>
      <VStack
        alignItems="center"
        maxWidth="100vw"
        pb={0}
        spacing={0}
        width="100%"
      >
        {header()}
        <Flex
          direction="column"
          gap={isPrint ? 2 : { base: 2, md: 2 }}
          mt={isPrint ? 0 : { base: 0, md: 0 }}
          width={isPrint ? '90%' : { base: '100%', md: '80%' }}
        >
          {isMobile && isViewOnly && (
            <Flex justifyContent="center" width="100%">
              <PreparedBy agent={sharedBy} />
            </Flex>
          )}
          {isMobile && !isPrint ? <ListingInfoMobile /> : <ListingInfo />}
          {renderContent()}
        </Flex>
      </VStack>
    </CopilotKit>
  );
};

const ReportContent: FC = () => {
  const { isLoadingReport, tables, includeIndigoData, isPrint } =
    useListingPageContext();

  const listingPageTables = isLoadingReport ? (
    <Skeleton height={METRIC_CARD_MIN_HEIGHT} width="100%" />
  ) : (
    tables?.map((table) => (
      <ListingPageKeyTermsTable
        key={table.title}
        showEmptyState={!includeIndigoData}
        tableData={table}
      />
    ))
  );

  const mlsDataSourceTag = (
    <Tag borderRadius="5px" color="white">
      MLS data only
    </Tag>
  );

  return (
    <>
      <ListingPageSection
        dataSource={
          <DataSourceTooltip label="MLS + Indigo Offer Data" size="md" />
        }
        id={MARKET_TRENDS_SECTION_ID}
        title={MARKET_TRENDS_SECTION_TITLE}
      >
        <ListingPageMetricsGrid />
      </ListingPageSection>
      <ListingPageSection
        dataSource={
          <DataSourceTooltip label="MLS + Indigo Offer Data" size="md" />
        }
        id={OFFER_TYPES_SECTION_ID}
        style={
          isPrint
            ? {
                breakBefore: 'page',
                marginTop: '30px',
              }
            : {}
        }
        title={OFFER_TYPES_SECTION_TITLE}
      >
        <VStack alignItems="stretch" spacing={4}>
          {listingPageTables}
        </VStack>
      </ListingPageSection>
      <ListingPageSection
        dataSource={mlsDataSourceTag}
        id={COMPARABLES_SECTION_ID}
        title={COMPARABLES_SECTION_TITLE}
      >
        <Comparables />
      </ListingPageSection>

      <ListingPageSection
        dataSource={mlsDataSourceTag}
        id={MLS_INFO_SECTION_ID}
        style={
          isPrint
            ? {
                breakBefore: 'page',
                marginTop: '30px',
              }
            : {}
        }
        title={MLS_INFO_SECTION_TITLE}
      >
        <MlsInfo />
      </ListingPageSection>
    </>
  );
};
